import { useCallback, useState, type MouseEvent } from 'react';
import type { CategoryId } from '../../../features/catalog/model';
import { BandLogo } from './BandLogo';
import { DailyTrafficData } from './DailyTrafficData';
import { DayTooltip } from './DayTooltip';
import { fmtTraffic, labelFor, monthLabel, type DailyTraffic } from './dailySeries';

/** Gap between columns, in pixels. Two is the density Kevin picked: the run
 *  still reads as one continuous record, and each day is separable by eye. */
const GAP = 2;
/** Plot height in pixels, from the same pick: tall enough that the shape reads
 *  from across the room. */
const HEIGHT = 340;

export interface DailyStackProps {
  traffic: DailyTraffic;
  /** catalog category per catalog card id, for the platform marks */
  categories: Record<string, CategoryId>;
}

/**
 * The network's daily request volume, one column per day, stacked by platform,
 * with a key of each platform's colour, real logo, name and window total.
 *
 * Hovering anywhere over the plot reads out that day rather than requiring the
 * pointer to land on a column: at this density a column is a few pixels wide,
 * so the whole plot is the target and the day is chosen from the pointer's
 * position across it. A band marks the day behind the run and the card carries
 * its whole split, which is the thing worth hovering for. The columns keep
 * their own colour: dimming the other eighty-eight to point at one made the
 * whole chart flinch on every pointer move.
 *
 * Hover state starts empty on the server and on the first client render, so the
 * two agree.
 */
export const DailyStack = ({ traffic, categories }: DailyStackProps) => {
  const { unit, readings, totals, hues, step, lines, ceiling } = traffic;
  const [at, setAt] = useState<number | null>(null);
  const rungs = Array.from({ length: lines }, (_, i) => i + 1);

  const onMove = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    if (box.width === 0) return;
    const ratio = (event.clientX - box.left) / box.width;
    const index = Math.floor(ratio * readings.length);
    setAt(Math.min(readings.length - 1, Math.max(0, index)));
  }, [readings.length]);

  const reading = at === null ? null : readings[at];
  const atPct = at === null ? 0 : ((at + 0.5) / readings.length) * 100;

  return (
    <div>
      {/* The drawing carries no text, so the record it draws is stated beside
          it for everything that cannot see. The plot and its axis are then
          decorative: leaving them readable would make an agent read a column of
          axis labels as if they were the data. */}
      <DailyTrafficData traffic={traffic} />
      <div aria-hidden className="flex items-end" style={{ height: HEIGHT }}>
        <div className="relative mr-3 h-full w-14 shrink-0">
          {[...rungs].reverse().concat(0).map((n) => (
            <span
              key={n}
              className="mono absolute right-0 -translate-y-1/2 text-10 text-text-3"
              style={{ top: `${(1 - n / lines) * 100}%` }}
            >
              {n === 0 ? fmtTraffic(unit, 0) : fmtTraffic(unit, step * n)}
            </span>
          ))}
        </div>

        <div
          className="relative h-full flex-1"
          onMouseMove={onMove}
          onMouseLeave={() => setAt(null)}
        >
          {rungs.map((n) => (
            <span
              key={n}
              className="absolute inset-x-0 h-px bg-line-2"
              style={{ top: `${(1 - n / lines) * 100}%` }}
            />
          ))}

          {at === null ? null : (
            <span
              className="absolute inset-y-0"
              style={{
                left: `${(at / readings.length) * 100}%`,
                width: `${100 / readings.length}%`,
                // The plot sits on the wash, so the marker cannot be the wash.
                // A tint of the brand accent reads on both themes without
                // competing with any platform hue in the stack.
                background: 'color-mix(in srgb, var(--accent) 12%, transparent)',
              }}
            />
          )}

          <div className="relative flex h-full items-end" style={{ gap: GAP }}>
            {readings.map((day) => (
              <div
                key={day.date}
                className="flex flex-1 flex-col-reverse"
                style={{ height: `${(day.total / ceiling) * 100}%` }}
              >
                {day.parts.map((part) => (
                  <span
                    key={part.id}
                    style={{
                      height: `${(part.value / (day.total || 1)) * 100}%`,
                      background: hues[part.id] ?? 'var(--c-slate)',
                    }}
                  />
                ))}
              </div>
            ))}
          </div>

          {reading ? (
            <DayTooltip
              reading={reading}
              unit={unit}
              hues={hues}
              atPct={atPct}
              categories={categories}
            />
          ) : null}
        </div>
      </div>

      <div aria-hidden className="ml-17 flex" style={{ gap: GAP }}>
        {readings.map((day) => (
          <span key={day.date} className="relative flex-1">
            {monthLabel(day.date) ? (
              <span className="mono absolute left-0 top-1.5 whitespace-nowrap text-10 text-text-3">
                {monthLabel(day.date)}
              </span>
            ) : null}
          </span>
        ))}
      </div>

      {/* The key to the colours. Its figures are the platform totals the text
          alternative above already states, so it is hidden from everything
          that reads that instead. */}
      <div aria-hidden className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
        {totals.map((part) => (
          <span key={part.id} className="flex items-center gap-2 text-12 text-text-2">
            <span
              className="h-3 w-1.5 rounded-2"
              style={{ background: hues[part.id] ?? 'var(--c-slate)' }}
            />
            <BandLogo id={part.id} categories={categories} size={16} />
            {labelFor(part.id)}
            <span className="mono text-text-3">{fmtTraffic(unit, part.value)}</span>
          </span>
        ))}
      </div>
    </div>
  );
};
